import { useState, useRef, useEffect, useMemo } from 'react';
import { Trash2, Pencil, Check, X, Search } from 'lucide-react';
import { Add, NotificationStatus } from 'iconsax-react';
import { Skeleton } from '@design-system/skeleton';
import { Dialog, DialogContent, DialogTrigger } from '@design-system/dialog';
import { DeleteConfirmDialog } from '@design-system/molecules/delete-confirm-dialog-button';
import {
  useConversations,
  useDeleteConversation,
  useRenameConversation,
} from '../hooks/use-conversations';
import { useAssistantStore } from '../store/assistant-store';
import type { ConversationSurface } from '../types/assistant.types';
import { nearEdge } from '@utils/near-edge';
import { useFillViewport } from '@hooks/use-fill-viewport';

function InlineRename({
  id,
  current,
  onDone,
}: {
  id: number;
  current: string | null;
  onDone: () => void;
}) {
  const [value, setValue] = useState(current ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const rename = useRenameConversation();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    const trimmed = value.trim();
    if (trimmed) rename.mutate({ id, title: trimmed });
    onDone();
  };
  const cancel = () => onDone();

  return (
    <div className='flex flex-1 items-center gap-1'>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') cancel();
        }}
        className='flex-1 rounded-[10px] border border-primary-200 px-2 py-0.5 text-sm text-gray-1000 focus:outline-none focus:ring-1 focus:ring-primary-1000'
        aria-label='Rename conversation'
      />
      <button
        type='button'
        aria-label='Confirm rename'
        onClick={submit}
        className='text-primary-1000 hover:text-primary-900'
      >
        <Check className='size-4' />
      </button>
      <button
        type='button'
        aria-label='Cancel rename'
        onClick={cancel}
        className='text-gray-400 hover:text-gray-600'
      >
        <X className='size-4' />
      </button>
    </div>
  );
}

/**
 * One row. Delete lives here so its confirm/pending/error state is per-row —
 * the same shape KnowledgeRow uses, and the reason the row can hide itself on
 * success instead of waiting for a refetch to return a shorter list.
 */
function ConversationRow({
  id,
  title,
  surface,
  isActive,
  onOpen,
  onDeleted,
}: {
  id: number;
  title: string | null;
  surface?: ConversationSurface;
  isActive: boolean;
  onOpen: () => void;
  onDeleted: (id: number) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const del = useDeleteConversation();
  const displayTitle = title ?? 'Untitled';

  return (
    <div
      data-slot='conversation-row'
      className={`group flex h-10 items-center justify-between rounded-full px-3 py-2 transition-colors ${
        isActive ? 'bg-primary-100' : 'bg-transparent hover:bg-primary-50'
      }`}
    >
      {renaming ? (
        <InlineRename
          id={id}
          current={title}
          onDone={() => setRenaming(false)}
        />
      ) : (
        <>
          <button
            type='button'
            className='min-w-0 flex-1 truncate text-left text-base text-gray-800'
            onClick={onOpen}
          >
            {displayTitle}
          </button>
          {surface === 'WHATSAPP' && (
            <span
              data-slot='conversation-surface'
              className='mr-2 shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-[11px] text-primary-1000'
            >
              WhatsApp
            </span>
          )}
          {del.isError && (
            <p
              role='alert'
              className='mr-2 truncate text-[11px] text-red-500'
            >
              {del.error.message}
            </p>
          )}
          <div className='flex shrink-0 items-center gap-1 opacity-0 group-hover:opacity-100'>
            <button
              type='button'
              aria-label='Rename'
              onClick={() => setRenaming(true)}
              className='text-gray-400 hover:text-primary-1000'
            >
              <Pencil className='size-4' />
            </button>
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type='button'
                  aria-label='Delete'
                  disabled={del.isPending}
                  className='text-gray-400 hover:text-red-500 disabled:opacity-50'
                >
                  <Trash2 className='size-4' />
                </button>
              </DialogTrigger>
              <DialogContent
                align='center'
                aria-describedby={undefined}
              >
                <DeleteConfirmDialog
                  title={
                    <span className='text-center text-gray-700 whitespace-pre-line'>
                      Are you sure you want to delete &ldquo;{displayTitle}
                      &rdquo;?
                      {'\n'}
                      Permanently erase the messages and saved execution state,
                      including memories quoted from this conversation. This
                      cannot be undone.
                    </span>
                  }
                  isLoading={del.isPending}
                  onConfirm={(closeModal) =>
                    del.mutate(id, {
                      onSuccess: () => {
                        onDeleted(id);
                        closeModal();
                      },
                      onError: () => closeModal(),
                    })
                  }
                />
              </DialogContent>
            </Dialog>
          </div>
        </>
      )}
    </div>
  );
}

export function ConversationList({
  canManageKnowledge = false,
}: {
  canManageKnowledge?: boolean;
}) {
  const { conversations, isLoading, loadMore, hasMore, loadingMore } =
    useConversations();
  const start = useAssistantStore((s) => s.startConversation);
  const setView = useAssistantStore((s) => s.setView);
  const activeId = useAssistantStore((s) => s.serverConversationId);
  const [query, setQuery] = useState('');
  // Result-driven removal: a deleted row hides on success
  // rather than waiting for a refetch, so a stale list can never resurrect it.
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<number>>(
    () => new Set()
  );
  const visible = useMemo(() => {
    const base = conversations?.filter((c) => !deletedIds.has(c.id)) ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((c) =>
      (c.title ?? 'Untitled').toLowerCase().includes(q)
    );
  }, [conversations, deletedIds, query]);
  const scrollRef = useRef<HTMLDivElement>(null);
  useFillViewport(
    scrollRef,
    hasMore,
    loadingMore,
    loadMore,
    conversations?.length ?? 0
  );

  return (
    <div className='flex h-full flex-col gap-2 p-3'>
      {canManageKnowledge && (
        <button
          type='button'
          onClick={() => setView('knowledge')}
          className='flex h-10 w-full items-center gap-2 rounded-full px-3 py-2 text-left text-base text-gray-800 transition-colors hover:bg-primary-50'
        >
          <NotificationStatus
            size={24}
            className='size-6 shrink-0 text-gray-800'
            variant='Outline'
          />
          <span className='min-w-0 flex-1 truncate'>Knowledge Base</span>
        </button>
      )}

      <button
        type='button'
        onClick={() => start(null)}
        className='flex h-10 w-full items-center gap-2 rounded-full px-3 py-2 text-left text-base text-gray-800 transition-colors hover:bg-primary-50'
      >
        <Add
          size={24}
          className='size-6 shrink-0 text-gray-800'
        />
        <span className='min-w-0 flex-1 truncate'>New Chat</span>
      </button>

      <div className='flex items-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 py-2.5'>
        <Search
          className='size-4 shrink-0 text-gray-800'
          aria-hidden='true'
        />
        <input
          type='search'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search ..'
          aria-label='Search conversations'
          className='min-w-0 flex-1 bg-transparent text-sm text-gray-800 placeholder:text-gray-800 focus:outline-none'
        />
      </div>

      <div
        ref={scrollRef}
        data-slot='conversation-scroll'
        className='flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto'
        onScroll={(e) => {
          if (hasMore && !loadingMore && nearEdge(e.currentTarget, 'bottom'))
            loadMore();
        }}
      >
        {isLoading && <Skeleton className='mb-1 h-10 w-full rounded-full' />}
        {visible.map((c) => (
          <ConversationRow
            key={c.id}
            id={c.id}
            title={c.title}
            surface={c.surface}
            isActive={c.id === activeId}
            onOpen={() => start(c.id, c.surface)}
            onDeleted={(id) => setDeletedIds((prev) => new Set(prev).add(id))}
          />
        ))}
        {loadingMore && (
          <Skeleton
            data-slot='conversation-loading-more'
            className='mb-1 h-10 w-full rounded-full'
          />
        )}
        {!isLoading && !visible.length && (
          <p className='mt-6 text-center text-sm text-gray-400'>
            No conversations yet.
          </p>
        )}
      </div>
    </div>
  );
}
